/* eslint-disable react-hooks/rules-of-hooks */
// 2022.09.14.01

import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { NextSeo } from "next-seo";
import PageQuery from "components/PageQuery";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import { mapBusinessItemData } from "lib/utils";
import styled, { css } from "styled-components";
import Pagination from "components/Pagination";
import SearchBar from "components/BusinessSearchBar";
import MapWithMarkers from "components/MapWithMarkers";
import ConditionalNavWrapper from "components/ConditionalNavWrapper";
import BusinessResultsHeader from "components/BusinessResultsHeader";
import BusinessSearchResult from "components/BusinessSearchResult";
import useCurrentLocation, { useFallbackLocation } from "hooks/use-current-location";
import useVendorUser from "hooks/use-vendor-user";
import useCustomerUser from "hooks/use-customer-user";
import ChangeRoleModal from "components/ChangeRoleModal";
import NoSearchResults from "components/NoSearchResults";
import { dataLayerViewItemList } from "lib/data-layer-push";

const OuterSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100vw;

  ${({ theme }) => theme.mq.xl`
    display: grid;
    grid-template-rows: auto 1fr;
  `}
`;

const ListAndMapContainer = styled.div`
  position: relative;
  transition: ${({ theme }) => theme.opacityTransition};
  width: 100vw;
  z-index: 1;

  ${({ theme }) => theme.mq.xl`
    display: grid;
    grid-template-columns: 1.3fr 1fr;
    grid-template-areas: "list map";
  `}
`;

const ResultListContainer = styled.div`
  grid-area: list;
  min-height: 100vh;
  display: ${({ activeView }) => (activeView === "list" && "flex") || "none"};
  flex: 1 0 auto;
  flex-direction: column;
  opacity: 1;
  transition: opacity 0.2s;
  transform: ${({ activeView }) => (activeView === "list" && "none") || "translate3d(100vw, 0, 0)"};
  width: 100vw;

  ${({ isLoading }) =>
    isLoading &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}

  ${({ theme }) => theme.mq.xl`
    display: flex;
    transform: none;
    width: auto;
  `}
`;

const GoogleMapContainer = styled.div`
  grid-area: map;
  height: ${({ activeView }) => (activeView === "map" && "calc(100vh - 23.125rem)") || "0"};
  left: 0;
  position: sticky;
  top: 0;
  transform: ${({ activeView }) => (activeView === "map" && "none") || "translate3d(100vw, 0, 0)"};
  width: 100vw;
  overflow: hidden;

  ${({ theme }) => theme.mq.md`
    height: ${({ activeView }) => (activeView === "map" && "calc(100vh - 13.5rem)") || "0"};
  `}

  ${({ theme }) => theme.mq.xl`
    border-left: 0.0625rem solid ${theme.themeColors.lightGrey};
    display: block;
    height: 100vh;
    margin: -8.25rem 0 0;
    transform: none;
    width: auto;
  `}
`;

const SearchResultsContainer = styled.ul`
  border-top: 0.0625rem solid ${({ theme }) => theme.themeColors.lightGrey};
  list-style-type: none;
  margin: 0;
  padding: 0;
  flex: 1 0 auto;
`;

const PaginationContainer = styled.div`
  border-top: 0.0625rem solid ${({ theme }) => theme.themeColors.lightGrey};
  margin: 0 1.5rem;
  padding: 2rem 0;
  position: relative;
`;

function Search() {
  const { push, query } = useRouter();
  const customerUser = useCustomerUser();
  const vendorUser = useVendorUser();
  const currentLocation = useCurrentLocation();
  const fallbackLocation = useFallbackLocation();
  const handleReadLoad = useRef(null);
  const [activeView, setActiveView] = useState("list");

  const mapLocation = {
    formattedAddress: query.formattedAddress || currentLocation?.formatted_address,
    latitude: query.latitude || currentLocation?.latitude,
    longitude: query.longitude || currentLocation?.longitude,
  };

  const startDateTime = useMemo(() => {
    const tomorrow = DateTime.now().plus({ days: 1 }).startOf("day");

    if (query.startDateTime) {
      const dt = DateTime.fromMillis(parseInt(query.startDateTime, 10));
      if (dt.toMillis() > tomorrow.toMillis()) {
        return dt;
      }
    }
    return tomorrow;
  }, [query.startDateTime]);

  const endDateTime = useMemo(() => startDateTime.plus({ days: 3 }), [startDateTime]);

  const mobileDateArr = useMemo(() => {
    const arr = [];
    arr.push(startDateTime);
    return arr;
  }, [startDateTime]);
  const s = query.s || "all";
  const a = query.a || false;
  const initialLatitudeDisplay = query.latitude || currentLocation?.latitude || fallbackLocation.latitude;
  const initialLongitudeDisplay = query.longitude || currentLocation?.longitude || fallbackLocation.longitude;
  const initialSearchString = query.q || "";
  const currentPage = query.currentPage || 1;
  const formattedAddress = query.formattedAddress || currentLocation?.formatted_address || fallbackLocation.formatted_address;
  const ps = query.pageSize || 20;
  const radius = query.radius || 50;
  const sort = query.sort || "";
  const view = query.view || "list";
  const queryObj = {
    currentPage,
    ...(ps && { pageSize: ps }),
    q: initialSearchString,
    s,
    a,
    latitude: initialLatitudeDisplay,
    longitude: initialLongitudeDisplay,
    startDateTime,
    endDateTime,
    formattedAddress,
    radius,
    sort,
    view,
  };

  return (
    <ConditionalNavWrapper>
      <NextSeo title="GoodFriend | Search" description={`Search Results in ${formattedAddress}`} />
      <PageQuery
        ns="search"
        userType="customer"
        readMethod="patch"
        onReadLoad={() => handleReadLoad?.current?.()}
        query={{
          vendorBusinesses: {
            fields: [
              "businessName",
              "businessType",
              "claimedDate",
              "phone",
              "timezoneAbbreviation",
              "time_zone_id",
              "friendlyName",
              "latitude",
              "longitude",
              "mediumImageUrl",
              "channelTimeSlots",
              "vendorUserId",
              "firstAvailableTimeslot",
              "formattedAddress",
              "hideAddress",
              "isAcceptingBooking",
              "urlLocality",
              "urlRegion",
              "restricted",
              "bookingType",
              "bookingStatus",
              "neighborhood",
            ],
            filter: {
              ...(!query.visible && { restricted: "false" }),
              withServiceTypeNameSearchWithinDistance: queryObj,
            },
            paginate: { number: queryObj.currentPage, size: queryObj.pageSize },
            include: {
              vendorBusinessTags: {
                fields: ["flavor", "subtitle", "title", "icon", "category"],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                },
              },
              vendorBusinessCertifications: {
                fields: ["certificationId"],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                },
                include: {
                  certifications: {
                    fields: ["id", "name", "description", "image"],
                    filter: {
                      id: {
                        fromContext: "vendorBusinessCertification.certificationId",
                      },
                    },
                  },
                },
              },
              vendorBusinessOpenHours: {
                fields: ["dayOfWeek", "vendorBusinessId", "calcStartTime", "calcEndTime"],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                  onDayOfWeek: 1,
                },
              },
              serviceTypes: {
                fields: ["name", "vendorBusinessId"],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                },
              },
              channelTimeSlots: {
                readOnly: true,
                paginate: { number: 1, size: 999 },
                fields: ["vendorBusinessId", "pillDisplayStart", "pillDisplayEnd", "dayOfWeek", "availabilityChannelId"],
                sort: [{ field: "minutesSinceMidnightStart", direction: "asc" }],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                  inPeriod: {
                    start_datetime: queryObj.startDateTime.toISODate(),
                    end_datetime: queryObj.endDateTime.toISODate(),
                    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  },
                },
              },
              channelDateTimeSlots: {
                readOnly: true,
                paginate: { number: 1, size: 999 },
                fields: ["startDatetime", "endDatetime", "dayOfWeek", "tzDatetime", "pillDisplayStart", "pillDisplayEnd"],
                sort: [
                  {
                    field: "startDatetime",
                    direction: "asc",
                  },
                ],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                  inPeriod: {
                    start_datetime: startDateTime.toISODate(),
                    end_datetime: endDateTime.toISODate(),
                    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  },
                  uniqueByStartTime: null,
                },
              },
            },
          },
        }}
      >
        {({ data, loading }) => {
          const [activeBusinessId, setActiveBusinessId] = useState(null);
          const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);

          // when a new page loads, auto-select the first item in the list
          handleReadLoad.current = useCallback(() => {
            const nextActiveId = data?.vendorBusinesses?.data?.[0]?.id;
            // we need to wait for the map to initialize before setting the
            // active ID to trigger the pin update.
            setTimeout(() => {
              setActiveBusinessId(nextActiveId);
            }, 250);
          }, [data]);

          useEffect(() => {
            dataLayerViewItemList(data?.vendorBusinesses?.data || [], s);
          }, [data]);

          return (
            <OuterSearchContainer>
              <SearchBar />
              <BusinessResultsHeader
                selectedSortOption={query?.sort}
                recordCount={data?.vendorBusinesses?.pagination?.recordCount}
                a={a}
                setActiveView={setActiveView}
                activeView={activeView}
              />
              <ListAndMapContainer>
                <ResultListContainer isLoading={loading} activeView={activeView}>
                  <SearchResultsContainer>
                    {data?.vendorBusinesses?.data ? (
                      data?.vendorBusinesses?.data.map((biz) => {
                        return (
                          <BusinessSearchResult
                            visible={["t", "true", "1"].includes(query?.visible?.toLowerCase())}
                            activeBusinessId={activeBusinessId}
                            onHover={setActiveBusinessId}
                            biz={biz}
                            isCustomerUser={!!customerUser}
                            isVendorUser={!!vendorUser}
                            key={biz?.id}
                            mobileDateArr={mobileDateArr}
                            serviceTypeQueryParam={s}
                            setShowChangeRoleModal={setShowChangeRoleModal}
                            {...mapBusinessItemData(biz, biz?.id)}
                          />
                        );
                      })
                    ) : (
                      <NoSearchResults />
                    )}
                  </SearchResultsContainer>
                </ResultListContainer>
                <GoogleMapContainer activeView={activeView}>
                  <MapWithMarkers
                    data={data}
                    currentLocation={mapLocation}
                    activeBusinessId={activeBusinessId}
                    onMarkerClick={setActiveBusinessId}
                    loading={loading}
                    services="all"
                    setActiveBusinessId={setActiveBusinessId}
                  />
                </GoogleMapContainer>
              </ListAndMapContainer>
              {data?.vendorBusinesses?.data && (
                <PaginationContainer>
                  <Pagination
                    pageSizeOptions={[20, 40]}
                    setPageSize={(numberPageItems) => {
                      push({
                        query: {
                          ...query,
                          currentPage: 1,
                          pageSize: numberPageItems,
                        },
                      });
                    }}
                    handlePageSelection={(page) => {
                      push({
                        query: {
                          ...query,
                          currentPage: page,
                        },
                      });
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    {...data?.vendorBusinesses?.pagination}
                  />
                </PaginationContainer>
              )}
              <ChangeRoleModal show={showChangeRoleModal} close={() => setShowChangeRoleModal(false)} userTypeToAccess="customer" />
            </OuterSearchContainer>
          );
        }}
      </PageQuery>
    </ConditionalNavWrapper>
  );
}

export default Search;
